import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TelemetryService } from '../../../services/telemetry.service';
import { AppModelsService } from '../../../services/app-models.service';
import { TelemetryFrame, Breadcrumb, PerformanceFrame } from '../../../models/session.model';

@Injectable()
export class SessionDataService {
  private telemetryService = inject(TelemetryService);
  private appModelsService = inject(AppModelsService);

  // State
  private rawPerformanceFrames: any[] = [];
  
  public telemetryFrames$ = new BehaviorSubject<TelemetryFrame[]>([]);
  public performanceFrames$ = new BehaviorSubject<PerformanceFrame[]>([]);
  public breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  public networkRequests$ = new BehaviorSubject<any[]>([]);

  // Engine references needed for calculation
  public engineDuration = 0;
  
  // Loading states
  public isLoadingTelemetry = false;
  public isLoadingPerformance = false;
  public isLoadingNetworkRequests = false;
  
  public uploadingModel: { [objectNombre: string]: boolean } = {};

  private currentTelemetryPage = 1;
  private currentPerformancePage = 1;
  private currentBreadcrumbsPage = 1;

  public dynamicObjectFirstFrame: Record<string, number> = {};

  public sceneSnapshot$ = new BehaviorSubject<any>(null);
  public pendingInitialSceneSnapshot = false;

  resetState() {
    this.rawPerformanceFrames = [];
    this.telemetryFrames$.next([]);
    this.performanceFrames$.next([]);
    this.breadcrumbs$.next([]);
    this.networkRequests$.next([]);
    this.sceneSnapshot$.next(null);
    this.currentTelemetryPage = 1;
    this.currentPerformancePage = 1;
    this.currentBreadcrumbsPage = 1;
    this.currentSceneSnapshotsPage = 1;
    this.pendingInitialSceneSnapshot = false;
    this.dynamicObjectFirstFrame = {};
    this.engineDuration = 0;
  }

  loadTelemetryProgressively(sessionId: string) {
    this.isLoadingTelemetry = true;
    const limit = 50; 
    this.telemetryService.getSessionTelemetry(sessionId, this.currentTelemetryPage, limit).subscribe(chunks => {
      if (chunks && chunks.length > 0) {
        let added = false;
        let currentFrames = this.telemetryFrames$.value;
        chunks.forEach(chunk => {
          if (chunk.frames && chunk.frames.length > 0) {
            const existing = new Set(currentFrames.map(f => f.timestampOffsetMs));
            const newFrames = chunk.frames.filter((f: any) => !existing.has(f.timestampOffsetMs));
            if (newFrames.length > 0) {
              currentFrames = [...currentFrames, ...newFrames];
              added = true;
            }
          }
        });
        
        if (added) {
          currentFrames.sort((a: any, b: any) => a.timestampOffsetMs - b.timestampOffsetMs);

          // Decompress delta-compressed dynamic objects
          let currentObjectsState: Record<string, any> = {};
          this.dynamicObjectFirstFrame = {}; 
          currentFrames.forEach((frame, i) => {
            if (frame.dynamicObjects) {
              for (const key of Object.keys(frame.dynamicObjects)) {
                currentObjectsState[key] = { ...frame.dynamicObjects[key] };
                if (this.dynamicObjectFirstFrame[key] === undefined) {
                  this.dynamicObjectFirstFrame[key] = i;
                }
              }
            }
            frame.dynamicObjects = JSON.parse(JSON.stringify(currentObjectsState));
          });

          const first = currentFrames[0].timestampOffsetMs;
          const last = currentFrames[currentFrames.length - 1].timestampOffsetMs;
          const newDuration = (last - first) / 1000;
          
          if (newDuration > this.engineDuration) {
            this.engineDuration = newDuration;
            if (!this.isLoadingPerformance) {
              this.decompressPerformanceFrames();
            }
          }

          this.telemetryFrames$.next(currentFrames);
        }
        
        if (chunks.length === limit) {
          this.currentTelemetryPage++;
          this.loadTelemetryProgressively(sessionId);
        } else {
          this.isLoadingTelemetry = false;
        }
      } else {
        this.isLoadingTelemetry = false;
      }
    }, error => {
      this.isLoadingTelemetry = false;
    });
  }

  loadPerformanceProgressively(sessionId: string) {
    this.isLoadingPerformance = true;
    const limit = 50; 
    this.telemetryService.getSessionPerformance(sessionId, this.currentPerformancePage, limit).subscribe(chunks => {
      if (chunks && chunks.length > 0) {
        let added = false;
        chunks.forEach(chunk => {
          if (chunk.items && chunk.items.length > 0) {
            const existing = new Set(this.rawPerformanceFrames.map(f => f.timestampOffsetMs));
            const newItems = chunk.items.filter((f: any) => !existing.has(f.timestampOffsetMs));
            if (newItems.length > 0) {
              this.rawPerformanceFrames = [...this.rawPerformanceFrames, ...newItems];
              added = true;
            }
          }
        });
        if (added) {
          this.rawPerformanceFrames.sort((a, b) => a.timestampOffsetMs - b.timestampOffsetMs);
        }

        if (chunks.length === limit) {
          this.currentPerformancePage++;
          this.loadPerformanceProgressively(sessionId);
        } else {
          this.isLoadingPerformance = false;
          this.decompressPerformanceFrames();
        }
      } else {
        this.isLoadingPerformance = false;
        this.decompressPerformanceFrames();
      }
    });
  }

  loadBreadcrumbsProgressively(sessionId: string) {
    const limit = 50;
    this.telemetryService.getSessionBreadcrumbs(sessionId, this.currentBreadcrumbsPage, limit).subscribe(chunks => {
      if (chunks && chunks.length > 0) {
        let added = false;
        let currentBreadcrumbs = this.breadcrumbs$.value;
        chunks.forEach(chunk => {
          if (chunk.items && chunk.items.length > 0) {
            const existing = new Set(currentBreadcrumbs.map(b => b.timestamp));
            const newItems = chunk.items.filter((b: any) => !existing.has(b.timestamp));
            if (newItems.length > 0) {
              currentBreadcrumbs = [...currentBreadcrumbs, ...newItems];
              added = true;
            }
          }
        });
        if (added) {
          currentBreadcrumbs.sort((a: any, b: any) => a.timestamp - b.timestamp);
          this.breadcrumbs$.next(currentBreadcrumbs);
        }
        if (chunks.length === limit) {
          this.currentBreadcrumbsPage++;
          this.loadBreadcrumbsProgressively(sessionId);
        }
      }
    });
  }

  loadNetworkRequestsProgressively(sessionId: string, page = 1) {
    this.isLoadingNetworkRequests = true;
    const limit = 50;
    this.telemetryService.getSessionNetworkRequests(sessionId, page, limit).subscribe({
      next: (chunks) => {
        if (chunks && chunks.length > 0) {
          let added = false;
          let currentReqs = this.networkRequests$.value;
          chunks.forEach(chunk => {
            if (chunk.items && chunk.items.length > 0) {
              currentReqs = [...currentReqs, ...chunk.items];
              added = true;
            }
          });
          if (added) {
            currentReqs.sort((a, b) => a.timestampOffsetMs - b.timestampOffsetMs);
            this.networkRequests$.next(currentReqs);
          }
          if (chunks.length === limit) {
            this.loadNetworkRequestsProgressively(sessionId, page + 1);
          } else {
            this.isLoadingNetworkRequests = false;
          }
        } else {
          this.isLoadingNetworkRequests = false;
        }
      },
      error: () => {
        this.isLoadingNetworkRequests = false;
      }
    });
  }

  private currentSceneSnapshotsPage = 1;

  loadSceneSnapshotsProgressively(sessionId: string, sessionStart: number) {
    const limit = 50;
    this.telemetryService.getSessionSceneSnapshots(sessionId, this.currentSceneSnapshotsPage, limit).subscribe(chunks => {
      if (chunks && chunks.length > 0) {
        chunks.forEach(chunk => {
          if (chunk.snapshots && chunk.snapshots.length > 0) {
            let currentSnapshot = this.sceneSnapshot$.value;
            if (!currentSnapshot) {
              currentSnapshot = chunk.snapshots[0];
              const t = currentSnapshot!.capturedAt || 0;
              const offset = t > 1000000000000 ? t - sessionStart : t;
              currentSnapshot!.objects.forEach((o: any) => o.spawnTimeMs = Math.max(0, offset));
              this.sceneSnapshot$.next(currentSnapshot);
              this.pendingInitialSceneSnapshot = false;
            } else {
              let addedNew = false;
              const existingNombres = new Set(currentSnapshot.objects.map((o: any) => o.name));
              for (const snap of chunk.snapshots) {
                const t = snap.capturedAt || 0;
                const offset = t > 1000000000000 ? t - sessionStart : t;
                for (const obj of snap.objects) {
                  if (!existingNombres.has(obj.name)) {
                    existingNombres.add(obj.name);
                    (obj as any).spawnTimeMs = Math.max(0, offset);
                    currentSnapshot.objects.push(obj);
                    addedNew = true;
                  }
                }
              }
              if (addedNew) {
                this.sceneSnapshot$.next({ ...currentSnapshot });
              }
            }
          }
        });
        if (chunks.length === limit) {
          this.currentSceneSnapshotsPage++;
          this.loadSceneSnapshotsProgressively(sessionId, sessionStart);
        }
      } else {
        this.pendingInitialSceneSnapshot = false;
      }
    });
  }

  linkModelUrl(session: any, obj: any, callback: () => void) {
    const url = prompt(`Ingresa la URL pública del modelo 3D (.glb o .gltf) para vincular a "${obj.name}":`, obj.modelUrl || '');
    if (url) {
      obj.modelUrl = url;
      
      const linkToApp = session?.appName && session?.app_version && confirm(`¿Deseas vincular este modelo a todas las sesiones de la aplicación ${session.appName} (v${session.app_version})?`);
      
      if (linkToApp) {
        this.appModelsService.linkAppModel(session.appName, session.app_version, obj.name, url).subscribe({
          next: () => {
            // Also update current session so it works immediately
            this.telemetryService.updateObjectModel(session.id, obj.name, url).subscribe({
              next: () => callback(),
              error: (err) => console.error('Error saving model URL', err)
            });
          },
          error: (err) => console.error('Error linking app model', err)
        });
      } else {
        this.telemetryService.updateObjectModel(session.id, obj.name, url).subscribe({
          next: () => callback(),
          error: (err) => console.error('Error saving model URL', err)
        });
      }
    }
  }

  async uploadModelFile(session: any, obj: any, file: any, callback: () => void) {
    if (!file) return;

    this.uploadingModel[obj.name] = true;

    this.telemetryService.uploadModel(session.id, obj.name, file).subscribe({
      next: (response) => {
        obj.modelUrl = response.url;
        
        const linkToApp = session?.appName && session?.app_version && confirm(`¿Deseas vincular este modelo a todas las sesiones de la aplicación ${session.appName} (v${session.app_version})?`);
        
        if (linkToApp) {
          this.appModelsService.linkAppModel(session.appName, session.app_version, obj.name, response.url).subscribe({
            next: () => {
              this.telemetryService.updateObjectModel(session.id, obj.name, response.url).subscribe({
                next: () => {
                  this.uploadingModel[obj.name] = false;
                  callback();
                },
                error: (err) => {
                  console.error('Error saving model URL to backend', err);
                  this.uploadingModel[obj.name] = false;
                }
              });
            },
            error: (err) => {
              console.error('Error linking app model', err);
              this.uploadingModel[obj.name] = false;
            }
          });
        } else {
          this.telemetryService.updateObjectModel(session.id, obj.name, response.url).subscribe({
            next: () => {
              this.uploadingModel[obj.name] = false;
              callback();
            },
            error: (err) => {
              console.error('Error saving model URL to backend', err);
              this.uploadingModel[obj.name] = false;
            }
          });
        }
      },
      error: (error) => {
        console.error("Upload failed", error);
        alert("Error al subir el modelo: " + error.message);
        this.uploadingModel[obj.name] = false;
      }
    });
  }


  decompressPerformanceFrames() {
    if (!this.rawPerformanceFrames || this.rawPerformanceFrames.length === 0) return;

    const decompressed: PerformanceFrame[] = [];
    const frames = this.rawPerformanceFrames;
    const maxMs = Math.max(this.engineDuration * 1000, frames[frames.length - 1].timestampOffsetMs);

    if (frames.length === 1 || maxMs === 0) {
      return;
    }

    let frameIdx = 0;
    for (let t = 0; t <= maxMs; t += 1000) {
      while (frameIdx < frames.length - 1 && frames[frameIdx + 1].timestampOffsetMs < t) {
        frameIdx++;
      }

      const f1 = frames[frameIdx];
      const f2 = frameIdx < frames.length - 1 ? frames[frameIdx + 1] : f1;

      if (f1 === f2 || t <= f1.timestampOffsetMs) {
        decompressed.push({ ...f1, timestampOffsetMs: t });
      } else {
        const range = f2.timestampOffsetMs - f1.timestampOffsetMs;
        const ratio = range > 0 ? (t - f1.timestampOffsetMs) / range : 0;

        decompressed.push({
          timestampOffsetMs: t,
          fps: Math.round(this.lerp(f1.fps, f2.fps, ratio)),
          cpuLevel: Math.round(this.lerp(f1.cpuLevel || 0, f2.cpuLevel || 0, ratio)),
          gpuLevel: Math.round(this.lerp(f1.gpuLevel || 0, f2.gpuLevel || 0, ratio)),
          ramUsageMb: Math.round(this.lerp(f1.ramUsageMb || 0, f2.ramUsageMb || 0, ratio)),
          networkRxBytes: Math.round(this.lerp(f1.networkRxBytes || 0, f2.networkRxBytes || 0, ratio)),
          networkTxBytes: Math.round(this.lerp(f1.networkTxBytes || 0, f2.networkTxBytes || 0, ratio)),
          refreshRate: Math.round(this.lerp(f1.refreshRate || 0, f2.refreshRate || 0, ratio)),
          batteryLevel: Math.round(this.lerp(f1.batteryLevel || 0, f2.batteryLevel || 0, ratio)),
          cpuCycleTimeMs: this.lerp(f1.cpuCycleTimeMs || 0, f2.cpuCycleTimeMs || 0, ratio),
          gpuCycleTimeMs: this.lerp(f1.gpuCycleTimeMs || 0, f2.gpuCycleTimeMs || 0, ratio)
        });
      }
    }

    this.performanceFrames$.next(decompressed);
  }

  private lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }
}
